/**
 * Weed & Feed product catalog + regional timing data for the
 * /tools/weed-and-feed-calculator tool. Pure data, no side effects.
 *
 * A weed & feed is a combination product: a granular (or liquid) fertilizer
 * carrying a post-emergent herbicide. The herbicide FAMILY is the safety-
 * critical field — phenoxy (2,4-D + mecoprop + dicamba) burns St. Augustine
 * and centipede; atrazine burns cool-season fescue and bluegrass. The repo
 * previously encoded that rule only in blog prose; this file makes it
 * structural via `safeGrassTypes`.
 *
 * Product coverage / bag weights verified via PAAPI (npm run paapi:lookup);
 * spreader settings verified against manufacturer charts (see per-product
 * source notes). Where a value could not be verified it is marked ASSUMED.
 */

/** Grass identifiers used by the calculator's safety gate. Intentionally a
 *  small, tool-local union (not the site's display-name grass keys) so the
 *  safety matrix stays readable; map to display names via GRASS_DISPLAY. */
export type GrassId = 'bermuda' | 'zoysia' | 'staug' | 'centipede' | 'fescue' | 'kbg'

export const GRASS_OPTIONS: { id: GrassId; label: string }[] = [
  { id: 'bermuda', label: 'Bermuda' },
  { id: 'zoysia', label: 'Zoysia' },
  { id: 'staug', label: 'St. Augustine' },
  { id: 'centipede', label: 'Centipede' },
  { id: 'fescue', label: 'Tall Fescue' },
  { id: 'kbg', label: 'Kentucky Bluegrass' },
]

export const GRASS_DISPLAY: Record<GrassId, string> = {
  bermuda: 'Bermuda',
  zoysia: 'Zoysia',
  staug: 'St. Augustine',
  centipede: 'Centipede',
  fescue: 'Tall Fescue',
  kbg: 'Kentucky Bluegrass',
}

/** phenoxy = 2,4-D-family broadleaf herbicide; atrazine = triazine (southern). */
export type WeedFeedHerbicideFamily = 'phenoxy' | 'atrazine'

export const FAMILY_LABEL: Record<WeedFeedHerbicideFamily, string> = {
  phenoxy: '2,4-D',
  atrazine: 'Atrazine',
}

export interface WeedFeedBagOption {
  coverageSqFt: number
  weightLbs: number
}

export interface WeedFeedLiquidRate {
  /** fluid oz of concentrate per 1,000 sq ft */
  ozPer1000SqFt: number
  /** sq ft one full container treats (drives the container count) */
  coveragePerContainerSqFt: number
  containerOz: number
  containerLabel: string
}

export interface WeedFeedSpreaderSetting {
  spreader: string
  setting: string
}

export interface WeedAndFeedProduct {
  id: string
  name: string
  /** Short label for the result card header. */
  short: string
  /** Amazon ASIN, or null when there is no reliable single listing. */
  asin: string | null
  /** Amazon media image id (the `I/<id>` segment) for the product thumbnail. */
  imageId?: string
  form: 'granular' | 'liquid'
  npk: [number, number, number]
  herbicideFamily: WeedFeedHerbicideFamily
  bagOptions?: WeedFeedBagOption[]
  liquidRate?: WeedFeedLiquidRate
  spreaderSettings?: WeedFeedSpreaderSetting[] | null
  /** Grass types this product is safe on (the structural safety gate). */
  safeGrassTypes: GrassId[]
  /** post-emergent products go on actively growing weeds; a pre-emergent
   *  combo (Triple Action) applies earlier, before crabgrass germinates. */
  timingModel: 'post-emergent' | 'pre-emergent'
  constraints: string[]
  /** One-line reason shown when this product is the recommendation. */
  why: string
}

/**
 * Seed catalog. Coverage / weight / NPK verified via PAAPI where an ASIN
 * exists. Spreader settings sourced from manufacturer spreader-settings
 * charts; entries marked ASSUMED still need a published-chart confirmation.
 */
export const WEED_AND_FEED_PRODUCTS: WeedAndFeedProduct[] = [
  {
    id: 'wf3',
    name: 'Scotts Turf Builder Weed & Feed 3',
    short: 'Weed & Feed 3 · 28-0-3',
    asin: 'B00XJM8KMQ',
    imageId: '41IVt6FtH2L',
    form: 'granular',
    npk: [28, 0, 3],
    herbicideFamily: 'phenoxy',
    bagOptions: [
      { coverageSqFt: 5000, weightLbs: 14.29 },
      { coverageSqFt: 15000, weightLbs: 42.87 },
    ],
    // Verified: rotary/broadcast 3½, drop 5 (Scotts label). Elite/Wizz dial
    // numbers are not published for this product, so they are omitted.
    spreaderSettings: [
      { spreader: 'Rotary / EdgeGuard broadcast', setting: '3½' },
      { spreader: 'Drop spreader', setting: '5' },
    ],
    safeGrassTypes: ['bermuda', 'zoysia', 'fescue', 'kbg'],
    timingModel: 'post-emergent',
    constraints: [
      'Apply to wet or dew-damp grass so granules stick to weed leaves',
      'Do not water in for 24 hours; no rain within 24 hours; between 60 and 90°F',
      'Wait until new grass has been mowed 3-4 times; maximum 2 applications per year',
    ],
    why: 'Best all-round pick for northern and transition-zone lawns',
  },
  {
    id: 'ta',
    name: 'Scotts Turf Builder Triple Action',
    short: 'Triple Action · 25-0-2',
    asin: 'B09JVFT7N5',
    imageId: '51in7bV-LRL',
    form: 'granular',
    // Retail listings (Lowe's/HD) for this ASIN show 25-0-2.
    npk: [25, 0, 2],
    herbicideFamily: 'phenoxy',
    // Verified via Lowe's: 4,000 sq ft (11.31 lb) and 12,000 sq ft (33.94 lb, SKU 26019).
    bagOptions: [
      { coverageSqFt: 4000, weightLbs: 11.31 },
      { coverageSqFt: 12000, weightLbs: 33.94 },
    ],
    // Published dial numbers vary by bag/variant, so we point to the label
    // rather than print an unverified setting.
    spreaderSettings: [{ spreader: 'Rotary / drop spreader', setting: 'See bag label' }],
    safeGrassTypes: ['bermuda', 'zoysia', 'fescue', 'kbg'],
    timingModel: 'pre-emergent',
    constraints: [
      'Apply to a dry lawn, then water in the pre-emergent barrier',
      'Do not seed for 4 months after application',
      'Maximum 2 applications per year',
    ],
    why: 'Adds crabgrass prevention, so its window runs earlier in spring',
  },
  {
    id: 'bonus',
    name: 'Scotts Turf Builder Bonus S Southern Weed & Feed',
    short: 'Bonus S · 29-0-10',
    asin: 'B00GPAGO6S',
    imageId: '51Jt7ysws4L',
    form: 'granular',
    npk: [29, 0, 10],
    herbicideFamily: 'atrazine',
    bagOptions: [
      { coverageSqFt: 5000, weightLbs: 17.24 },
      { coverageSqFt: 10000, weightLbs: 34.48 },
    ],
    // Verified: rotary/broadcast 4¼, drop 7¼ (Scotts label; some listings vary).
    spreaderSettings: [
      { spreader: 'Rotary / EdgeGuard broadcast', setting: '4¼' },
      { spreader: 'Drop spreader', setting: '7¼' },
    ],
    safeGrassTypes: ['staug', 'centipede', 'zoysia'],
    timingModel: 'post-emergent',
    constraints: [
      'Apply to wet grass in the morning, then water in',
      'Do not apply during winter dormancy; wait until after the 3rd mowing of the season',
      'Do not use on fescue, bluegrass, or bermuda; maximum 2 applications per year',
    ],
    why: 'The go-to for St. Augustine and centipede lawns',
  },
  // NOTE: two products are intentionally held out of v1 pending label
  // verification (per the "verify before ship" call):
  //   - Scotts "Southern Triple Action": no confirmed distinct retail SKU / ASIN.
  //   - BioAdvanced 3-in-1 Southern (ASIN B0BR8J59CL): its herbicide is actually
  //     2,4-D/quinclorac/dicamba (phenoxy) formulated St.-Augustine-safe, NPK is
  //     unpublished, coverage is only derived, and it is max 1 application/year.
  //     Re-add once NPK + coverage are confirmed and the family/safety model can
  //     represent a St.-Aug-safe phenoxy product without misleading the gate.
  {
    id: 'fert',
    name: 'Fertilome Centipede Weed & Feed 15-0-15',
    short: 'Fertilome 15-0-15',
    asin: 'B0CYJMR85M',
    imageId: '51WYEpDs6SL',
    form: 'granular',
    npk: [15, 0, 15],
    herbicideFamily: 'atrazine',
    bagOptions: [{ coverageSqFt: 5000, weightLbs: 16 }],
    spreaderSettings: [{ spreader: 'Rotary / broadcast', setting: 'See bag label' }],
    safeGrassTypes: ['centipede', 'staug'],
    timingModel: 'post-emergent',
    constraints: [
      'Apply to a moist lawn (morning dew or pre-wet)',
      'Do not water in; keep water off for 24 hours',
      'Centipede and St. Augustine lawns only; maximum 2 applications per year',
    ],
    why: 'Low-nitrogen blend built specifically for centipede',
  },
  {
    id: 'soag',
    name: 'Southern Ag Atrazine (liquid)',
    short: 'Southern Ag Atrazine · liquid',
    asin: 'B0052MZBYQ',
    imageId: '41xJ0HypHcL',
    form: 'liquid',
    npk: [0, 0, 0],
    herbicideFamily: 'atrazine',
    // Label: one 32 oz quart treats ~3,720 sq ft → ~8.6 oz concentrate / 1,000 sq ft.
    liquidRate: {
      ozPer1000SqFt: 8.6,
      coveragePerContainerSqFt: 3720,
      containerOz: 32,
      containerLabel: 'quart',
    },
    spreaderSettings: null,
    safeGrassTypes: ['staug', 'centipede'],
    timingModel: 'post-emergent',
    constraints: [
      'Spray when weeds are young and actively growing',
      'St. Augustine and centipede lawns only',
      'Maximum 2 applications per year, below 90°F',
    ],
    why: 'Straight liquid atrazine when you do not need the fertilizer',
  },
]

export function getWeedAndFeedProduct(id: string): WeedAndFeedProduct | undefined {
  return WEED_AND_FEED_PRODUCTS.find((p) => p.id === id)
}

/**
 * Weed & feed application windows per site region (keys match
 * getRegionFromZipCode output, including the fine-grained sub-regions).
 * `[start, end]` are "Month Day" strings resolved to concrete dates by the
 * engine. `spring`/`fall` are the post-emergent windows (weeds actively
 * growing); `preSpring` is the earlier pre-emergent window used by combo
 * products like Triple Action. Seeded from the design prototype and expanded
 * to the full region set; agronomic windows, not hard dates.
 */
export interface RegionTimingWindows {
  spring: [string, string]
  fall: [string, string]
  preSpring: [string, string]
  notes: string
}

export const WEED_FEED_REGION_TIMING: Record<string, RegionTimingWindows> = {
  Northeast: {
    spring: ['May 1', 'June 15'],
    fall: ['September 1', 'October 1'],
    preSpring: ['April 1', 'May 1'],
    notes: 'Wait for 2-3 mows; cool springs push the window later',
  },
  'Mid-Atlantic': {
    spring: ['April 15', 'June 1'],
    fall: ['September 1', 'October 15'],
    preSpring: ['March 15', 'April 20'],
    notes: 'Fall is the stronger window for fescue lawns',
  },
  Southeast: {
    spring: ['April 1', 'May 15'],
    fall: ['September 15', 'October 31'],
    preSpring: ['March 1', 'April 1'],
    notes: 'Check the atrazine rule for St. Augustine and centipede',
  },
  'Great Lakes': {
    spring: ['April 20', 'June 5'],
    fall: ['September 1', 'October 10'],
    preSpring: ['April 1', 'May 1'],
    notes: 'Skip applications during summer heat stress',
  },
  'Upper Midwest': {
    spring: ['May 1', 'June 15'],
    fall: ['August 25', 'October 1'],
    preSpring: ['April 10', 'May 10'],
    notes: "Short season; don't push past early October",
  },
  Central: {
    spring: ['April 10', 'May 25'],
    fall: ['September 5', 'October 15'],
    preSpring: ['March 20', 'April 25'],
    notes: 'Watch the wind on spray days',
  },
  'South Central': {
    spring: ['March 15', 'May 1'],
    fall: ['September 15', 'November 1'],
    preSpring: ['February 15', 'March 25'],
    notes: 'Stop once daytime highs pass 90°F',
  },
  'Mountain West': {
    spring: ['May 1', 'June 15'],
    fall: ['September 1', 'October 1'],
    preSpring: ['April 10', 'May 10'],
    notes: 'Water in well; dry air slows uptake',
  },
  'West Coast': {
    spring: ['April 1', 'May 30'],
    fall: ['September 15', 'November 1'],
    preSpring: ['March 1', 'April 10'],
    notes: 'Long, forgiving window along the coast',
  },
  California: {
    spring: ['March 15', 'May 15'],
    fall: ['September 15', 'November 1'],
    preSpring: ['February 15', 'March 25'],
    notes: 'Inland valleys run hotter; treat earlier in spring',
  },
  'Pacific Northwest': {
    spring: ['April 1', 'May 30'],
    fall: ['September 1', 'October 15'],
    preSpring: ['March 1', 'April 10'],
    notes: 'Wet springs delay the first mow; wait for active growth',
  },
  'Desert Southwest': {
    spring: ['March 1', 'April 20'],
    fall: ['September 15', 'October 31'],
    preSpring: ['February 1', 'March 10'],
    notes: 'Very short spring window before heat; stop above 90°F',
  },
  Florida: {
    spring: ['March 1', 'April 15'],
    fall: ['October 1', 'November 15'],
    preSpring: ['February 1', 'March 1'],
    notes: 'Nearly year-round growth; use atrazine products only',
  },
  Hawaii: {
    spring: ['March 1', 'May 1'],
    fall: ['September 1', 'November 1'],
    preSpring: ['February 1', 'March 15'],
    notes: 'Year-round warm-season growth; treat when weeds appear',
  },
  Alaska: {
    spring: ['May 20', 'June 20'],
    fall: ['August 10', 'September 5'],
    preSpring: ['May 1', 'May 25'],
    notes: 'Very short season; one spring pass is usually enough',
  },
}

export function getRegionTiming(region: string): RegionTimingWindows | null {
  return WEED_FEED_REGION_TIMING[region] ?? null
}
